import { getBrands } from "./brandsSource";
import { Domain } from "../types/domains";
import { type BrandConfig, brandConfigSchema } from "./brandConfigSchema";

/**
 * When set to true, getConfig() will return the remote config if it has been
 * fetched. Toggle this flag to enable remote config loading in the future.
 */
const USE_REMOTE_CONFIG = false;

let remoteConfig: BrandConfig | null = null;
let injectedConfig: BrandConfig | null = null;

export const getQueryParam = (param: string) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
};

export const setInjectedConfig = (config: BrandConfig): void => {
  injectedConfig = config;
};

export const getConfig = (): BrandConfig => {
  if (injectedConfig) return injectedConfig;

  if (USE_REMOTE_CONFIG && remoteConfig) {
    return remoteConfig;
  }

  const domain = getQueryParam("domain");
  const brands = getBrands();
  const brand = domain
    ? brands.find((brand) => brand.domains.some((d) => d.name === domain))
    : null;

  // Local configs are already validated at startup in combinedConfig.ts
  if (brand) {
    return brand.config;
  } else {
    return (
      brands.find((brand) => brand.name === "The Kooples")?.config ||
      brands?.[0]?.config
    );
  }
};

/**
 * Fetches a brand config from a remote endpoint for the given domain.
 * Call this early in the app lifecycle (before rendering) and await it.
 *
 * The endpoint is expected to return a JSON object matching the BrandConfig shape:
 *   GET {baseUrl}/{domain}
 *
 * Usage (future):
 *   await fetchRemoteConfig("https://api.kleep.ai/configs", "lacoste.com");
 */
export const fetchRemoteConfig = async (
  baseUrl: string,
  domain: string,
): Promise<BrandConfig> => {
  const response = await fetch(`${baseUrl}/${encodeURIComponent(domain)}`);
  if (!response.ok) {
    throw new Error(
      `Failed to fetch remote config for domain "${domain}": ${response.status}`,
    );
  }
  const raw = await response.json();
  // Validate remote configs at fetch time (not validated at startup)
  brandConfigSchema.safeParse(raw);
  const config = raw as BrandConfig;
  remoteConfig = config;
  return config;
};

/**
 * Returns whether a remote config has been loaded.
 */
export const hasRemoteConfig = (): boolean => remoteConfig !== null;

/**
 * Clears any previously fetched remote config, reverting to local configs.
 */
export const clearRemoteConfig = (): void => {
  remoteConfig = null;
};

export const findBrandByDomain = () => {
  const domain = getQueryParam("domain");

  if (!domain) return null;
  return getBrands().find((brand) =>
    brand.domains.some((d) => d.name === domain),
  );
};

export const findDomainData = (): Domain | null => {
  const domain = getQueryParam("domain");
  if (!domain) return null;

  for (const brand of getBrands()) {
    const matchedDomain = brand.domains.find((d) => d.name === domain);
    if (matchedDomain) {
      return matchedDomain;
    }
  }
  return null;
};
